/**
 * ReplicaTrainingPage — Daily Training Console
 * 
 * Surgical modification of existing page to add:
 * - Today's Training (2 minutes) card at top
 * - Daily Proof Panel with baseline vs replica comparison
 * - Keep existing Rules / Preferences / Examples tabs
 */

import React, { useEffect, useState, useCallback } from 'react';
import {
    Shield, BookOpen, AlertCircle, Plus, Brain, ArrowLeft,
    Clock, Zap, Briefcase, FileText, AlertTriangle, Check,
    Edit2, X, User, TrendingUp, Calendar, Sparkles
} from 'lucide-react';
import { getApiUrl } from '../lib/env';
import { surpriseCalculator } from '../lib/surprise';
import type { SurpriseMetrics } from '../lib/surprise';
import CognitiveDNAPanel from '../components/training/CognitiveDNAPanel';
import CoachingMotivation from '../components/training/CoachingMotivation';

// ============================================
// TYPES (inline for surgical modification)
// ============================================

type TrainingAction = "approve" | "edit_teach" | "reject_explain";
type CaseSource = "quick_case" | "from_work";
type FeedbackReason = "tone" | "missing_info" | "structure" | "policy" | "safety" | "other";
type PanelState = "idle" | "generating" | "ready" | "editing" | "completed";

interface ContextPack {
    toneGuide: boolean;
    policies: boolean;
    pastExamples: boolean;
    redZones: boolean;
}

interface DraftResult {
    text: string;
    issuesDetected: string[];
    estimatedEdits: number;
    estimatedTimeSeconds: number;
    guardianSignals: {
        requiresApproval: boolean;
        escalationTriggered: boolean;
        escalationReason?: string;
        surpriseScore?: number;
        confidenceLevel?: number;
    };
}

interface CaseEvaluation {
    representationQuality: number;
    styleMatch: number;
    correctness: number;
    safety: number;
    escalationAccuracy: number;
    edits: number;
    seconds: number;
    savingsSeconds: number;
    correctionReductionPct: number;
    riskFlagsAvoided: string[];
    whyReplicaWon: string[];
}

interface TrainingEvent {
    id: string;
    timestamp: string;
    caseId: string;
    action: TrainingAction;
    feedbackReasons: FeedbackReason[];
    feedbackNote?: string;
    editedReplicaText?: string;
    evaluation: CaseEvaluation;
}

interface Rule {
    id: string;
    description: string;
    category: string;
    isActive: boolean;
    createdAt: string;
}

interface TrainingData {
    rules: Rule[];
    preferences: any[];
    examples: any[];
}

// ============================================
// STORAGE UTILITIES
// ============================================

const STORAGE_KEYS = {
    TRAINING_EVENTS: 'hr_training_events',
    LAST_TRAINED_DATE: 'hr_last_trained_date',
};

function getTrainingEvents(): TrainingEvent[] {
    try {
        const data = localStorage.getItem(STORAGE_KEYS.TRAINING_EVENTS);
        return data ? JSON.parse(data) : [];
    } catch {
        return [];
    }
}

function saveTrainingEvent(event: TrainingEvent, replicaId?: string): void {
    // Save to localStorage (immediate, offline-first)
    const events = getTrainingEvents();
    events.unshift(event);
    const trimmed = events.slice(0, 30);
    localStorage.setItem(STORAGE_KEYS.TRAINING_EVENTS, JSON.stringify(trimmed));
    localStorage.setItem(STORAGE_KEYS.LAST_TRAINED_DATE, new Date().toISOString().split('T')[0]);

    // Persist to backend (fire-and-forget)
    if (replicaId) {
        const token = localStorage.getItem('token');
        fetch(`${getApiUrl()}/training/${replicaId}/events`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
            },
            body: JSON.stringify(event),
        }).catch(err => console.warn('Failed to persist training event to backend', err));
    }
}

function getLastTrainedDate(): string | null {
    return localStorage.getItem(STORAGE_KEYS.LAST_TRAINED_DATE);
}

function getLastTrainedLabel(): string {
    const lastDate = getLastTrainedDate();
    if (!lastDate) return 'never';

    const today = new Date().toISOString().split('T')[0];
    const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];

    if (lastDate === today) return 'Today';
    if (lastDate === yesterday) return 'Yesterday';
    return lastDate;
}

function getLast7DaysEvents(): TrainingEvent[] {
    const events = getTrainingEvents();
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - 7);
    return events.filter(e => new Date(e.timestamp) > cutoff);
}

// ============================================
// SCORING UTILITIES
// ============================================

function scoreCase(
    baseline: DraftResult,
    replica: DraftResult,
    contextPack: ContextPack,
    feedbackReasons: FeedbackReason[]
): CaseEvaluation {
    // Style Match
    let styleMatch = 100;
    if (feedbackReasons.includes('tone')) styleMatch -= 25;
    if (replica.issuesDetected.some(i => i.toLowerCase().includes('tone'))) styleMatch -= 15;
    styleMatch = Math.max(0, styleMatch);

    // Correctness
    let correctness = 100;
    if (feedbackReasons.includes('missing_info')) correctness -= 20;
    if (feedbackReasons.includes('structure')) correctness -= 15;
    if (feedbackReasons.includes('policy')) correctness -= 30;
    if (feedbackReasons.includes('safety')) correctness -= 40;
    correctness = Math.max(0, correctness);

    // Safety
    let safety = 100;
    if (feedbackReasons.includes('safety')) safety -= 50;
    if (feedbackReasons.includes('policy')) safety -= 20;
    if (contextPack.redZones && baseline.issuesDetected.some(i => i.toLowerCase().includes('forbidden'))) {
        safety = Math.max(safety, 80); // Avoided risk
    }
    safety = Math.max(0, Math.min(100, safety));

    // Escalation Accuracy
    let escalationAccuracy = 80;
    if (replica.guardianSignals.escalationTriggered) {
        if (feedbackReasons.includes('policy') || feedbackReasons.includes('safety')) {
            escalationAccuracy = 100; // Correctly triggered
        } else {
            escalationAccuracy = 80; // Conservative
        }
    } else {
        if (feedbackReasons.includes('policy') || feedbackReasons.includes('safety')) {
            escalationAccuracy = 50; // Missed
        }
    }

    const representationQuality = Math.round((styleMatch + correctness + safety + escalationAccuracy) / 4);

    const savingsSeconds = Math.max(0, baseline.estimatedTimeSeconds - replica.estimatedTimeSeconds);
    const correctionReductionPct = baseline.estimatedEdits > 0
        ? Math.round((1 - replica.estimatedEdits / baseline.estimatedEdits) * 100)
        : 0;

    const riskFlagsAvoided: string[] = [];
    if (contextPack.redZones) riskFlagsAvoided.push('Red zones respected');
    if (contextPack.policies) riskFlagsAvoided.push('Policies applied');
    if (replica.guardianSignals.escalationTriggered) riskFlagsAvoided.push('Guardian escalation handled');

    const whyReplicaWon: string[] = [];
    if (contextPack.toneGuide) whyReplicaWon.push('Used Tone Guide');
    if (contextPack.policies) whyReplicaWon.push('Applied Policies');
    if (contextPack.pastExamples) whyReplicaWon.push('Followed past examples');
    if (replica.guardianSignals.escalationTriggered) whyReplicaWon.push('Guardian escalation handled correctly');
    if (representationQuality > 70) whyReplicaWon.push('Higher representation quality than baseline');

    return {
        representationQuality,
        styleMatch,
        correctness,
        safety,
        escalationAccuracy,
        edits: replica.estimatedEdits,
        seconds: replica.estimatedTimeSeconds,
        savingsSeconds,
        correctionReductionPct,
        riskFlagsAvoided,
        whyReplicaWon
    };
}

function formatTime(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function generateId(): string {
    return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

// ============================================
// DEMO DRAFT GENERATION
// ============================================

const DEMO_CASES = [
    {
        title: 'Customer Refund Request',
        prompt: 'A long-time customer is requesting a refund for a product purchased 45 days ago, outside our 30-day return window. They claim the product was defective.',
        baselineText: `Dear Customer,

Thank you for reaching out. I understand you're requesting a refund for your recent purchase.

Unfortunately, our return policy states that all returns must be made within 30 days of purchase. Since your purchase was made 45 days ago, we are unable to process a refund at this time.

If you have any other questions, please don't hesitate to reach out.

Best regards,
Customer Service`,
        replicaText: `Dear [Customer Name],

Thank you for bringing this to our attention. I understand the frustration when a product doesn't meet expectations, and I appreciate you being a loyal customer.

While our standard return window is 30 days, I'd like to help resolve this situation. Given the defect claim and your history with us, I'm flagging this for manager review to explore options:

• Possible store credit
• Replacement product
• Extended warranty coverage

I'll follow up within 24 hours with a resolution. In the meantime, could you please share photos of the defect so we can document this properly?

Thank you for your patience.

Best regards`,
        baselineIssues: ['Tone too rigid for loyal customer', 'No acknowledgment of defect claim', 'Missing escalation path', 'No follow-up commitment'],
        replicaIssues: [],
        guardianTriggered: true,
        escalationReason: 'Refund outside policy window — requires manager approval'
    },
    {
        title: 'Urgent Project Timeline Query',
        prompt: 'The client is asking if we can deliver the project 2 weeks earlier than agreed. The team is already at capacity.',
        baselineText: `Hi,

Thanks for your message. Moving the deadline up by 2 weeks would be challenging given our current workload. We'd need to discuss this further with the team.

Let me know if you want to schedule a call.

Thanks`,
        replicaText: `Hi [Client Name],

Thank you for reaching out about the timeline. I understand the urgency and want to help find a solution.

After reviewing our current capacity, here are the options I can present:

**Option A: Phased Delivery**
Deliver core functionality by your requested date, with remaining features following the original timeline.

**Option B: Scope Adjustment**
Prioritize critical features for early delivery; defer non-essential items to phase 2.

**Option C: Resource Discussion**
If full acceleration is essential, we can discuss additional resource allocation (subject to cost adjustment).

I'll need to flag this for project lead review before committing. Could we schedule a 20-minute call this week to align on priorities?

Best regards`,
        baselineIssues: ['Vague response without concrete options', 'No structured alternatives', 'Missing commitment to follow up', 'Informal tone'],
        replicaIssues: [],
        guardianTriggered: true,
        escalationReason: 'Timeline change requires project lead approval'
    },
    {
        title: 'Confidential Data Sharing Request',
        prompt: 'A partner company is asking for access to our internal client database for a joint marketing campaign. They say it was approved verbally by someone in sales.',
        baselineText: `Hi,

Sure, I can arrange access to the client database. I'll send you the export file by end of day. Let me know what format works best for you.

Best`,
        replicaText: `Hi [Partner Name],

Thank you for your interest in collaborating on this campaign.

Before proceeding, I need to flag an important compliance requirement: sharing client data externally requires written authorization through our data governance process, regardless of verbal agreements.

Here's what I'll do:
1. Verify the approval with our sales team and get written confirmation
2. Loop in our Data Protection Officer for compliance review
3. Determine what data can be shared (anonymized vs. identifiable)

I'll have an update for you within 48 hours. In the meantime, I can share aggregate demographic insights that don't require the same approval level.

Best regards`,
        baselineIssues: ['Shares confidential data without verification', 'No compliance check', 'Accepts verbal approval for sensitive data', 'No DPO involvement', 'GDPR violation risk'],
        replicaIssues: [],
        guardianTriggered: true,
        escalationReason: 'Confidential data request — requires DPO and written authorization'
    },
    {
        title: 'Angry Employee Complaint',
        prompt: 'An employee sends a heated email accusing their manager of favoritism in promotion decisions. They threaten to go to social media if nothing is done.',
        baselineText: `Hello,

Thank you for sharing your concerns. We take all complaints seriously. I'll forward this to HR for review.

Please refrain from posting on social media as it could have consequences.

Regards`,
        replicaText: `Dear [Employee Name],

Thank you for bringing this to my attention. I understand this situation is frustrating, and I want you to know your concerns are heard.

I want to address this properly:

**Immediate steps:**
• I'm scheduling a confidential meeting with HR within the next 2 business days
• Your complaint will be documented formally and reviewed impartially
• You'll receive written acknowledgment of this complaint within 24 hours

**Important context:**
Our promotion process includes documented criteria and review panels. HR will examine whether proper procedures were followed in this case.

I understand the impulse to seek external resolution, but I'd encourage you to let our internal process work first — it's designed to protect you. Going public could limit the options available to resolve this in your favor.

Would you prefer to meet in person or via video call to discuss next steps?

Best regards`,
        baselineIssues: ['Dismissive tone', 'Threatening language about social media', 'No concrete action plan', 'No timeline', 'No empathy shown'],
        replicaIssues: [],
        guardianTriggered: true,
        escalationReason: 'HR escalation — employee complaint with external threat'
    },
    {
        title: 'Vendor Price Increase Notification',
        prompt: 'Our key software vendor just notified us of a 40% price increase effective next quarter. We depend heavily on their platform.',
        baselineText: `Hi team,

FYI — our vendor is increasing prices by 40% next quarter. We'll need to budget for the increase.

Let me know if you have questions.`,
        replicaText: `Hi [Team],

I'm writing regarding the 40% price increase notification from [Vendor]. Given our dependency on their platform, here's my recommended action plan:

**Short-term (this week):**
• Request a meeting with our vendor account manager to negotiate
• Review our contract for price protection clauses or cap provisions
• Get exact figures on the budget impact across all departments

**Medium-term (this month):**
• Evaluate competitive alternatives (I'll prepare a comparison matrix)
• Assess migration feasibility and costs vs. absorbing the increase
• Explore multi-year lock-in for a lower rate

**Decision needed by:** End of month to allow planning time before next quarter.

I'd like to flag this for leadership review — a 40% increase on a critical dependency warrants strategic discussion.

I'll circulate a detailed impact analysis by Friday.

Best regards`,
        baselineIssues: ['No action plan', 'Passive acceptance of increase', 'No negotiation strategy', 'Missing urgency', 'No alternatives explored'],
        replicaIssues: [],
        guardianTriggered: false,
        escalationReason: undefined
    },
    {
        title: 'Client Requesting Competitor Information',
        prompt: 'A potential client asks you to share detailed competitive analysis and pricing information about your three main competitors.',
        baselineText: `Hi,

Here's what I know about our competitors:
- Company A charges around $50/user, their product is slower
- Company B had data breach issues last year
- Company C is losing customers

Let me know if you need more details on any of these.

Best`,
        replicaText: `Hi [Client Name],

Thank you for your interest in understanding the market landscape. I appreciate the thorough approach to your evaluation.

Rather than commenting on specific competitors, I'd like to focus on what makes our solution the right fit for your needs:

**What sets us apart:**
• [Key differentiator 1 relevant to their use case]
• [Key differentiator 2 with measurable outcomes]
• [Customer success metrics from similar clients]

I can also provide:
• Case studies from companies in your industry
• A personalized demo tailored to your workflow
• References from clients who evaluated the same alternatives

Would any of these be helpful for your decision process? I'm happy to set up a call to walk through your specific requirements.

Best regards`,
        baselineIssues: ['Shares unverified competitor information', 'Potentially defamatory claims', 'Unprofessional approach', 'Legal liability risk'],
        replicaIssues: [],
        guardianTriggered: true,
        escalationReason: 'Competitor information request — legal and ethical review needed'
    },
    {
        title: 'Emergency System Outage Communication',
        prompt: 'Our main platform is down affecting 2,000+ users. The engineering team estimates 4 hours to fix. Customers are flooding support channels.',
        baselineText: `Subject: System Issue

Hi everyone,

We are experiencing some technical difficulties. Our team is working on it. We apologize for any inconvenience.

We will update you when it's resolved.

Thank you for your patience.`,
        replicaText: `Subject: [SERVICE NAME] — Current Outage & Recovery Timeline

Dear valued customers,

We are aware that our platform is currently unavailable and we sincerely apologize for the disruption to your operations.

**Current status:** Our engineering team identified the root cause at [TIME] and is actively implementing a fix.

**Estimated recovery:** We expect full service restoration within 4 hours (by [SPECIFIC TIME with timezone]).

**What you can do:**
• Your data is safe — no data loss has occurred
• Pending transactions will be processed automatically once service resumes
• For urgent needs, our support team is available via phone at [NUMBER]

**Next updates:**
• We will send a status update every 60 minutes
• A full post-incident report will be shared within 24 hours of resolution

We understand the impact this has on your work and take full responsibility. Thank you for your patience.

[Name], Head of Operations`,
        baselineIssues: ['Vague "technical difficulties" language', 'No timeline', 'No specifics on impact or data safety', 'No update schedule', 'No ownership taken'],
        replicaIssues: [],
        guardianTriggered: false,
        escalationReason: undefined
    },
    {
        title: 'Employee Requesting Salary Raise via Email',
        prompt: 'A high-performing team member sends an email requesting a 25% salary increase, citing competing offers from two other companies.',
        baselineText: `Hi [Name],

Thanks for letting me know. I'll pass this along to HR and see what we can do. Budget is tight this year so I can't promise anything.

Talk soon.`,
        replicaText: `Hi [Employee Name],

Thank you for sharing this with me directly — I appreciate your transparency, and I value your contributions to the team.

I want to handle this thoughtfully rather than give a rushed response. Here's what I'd like to propose:

**Immediate next steps:**
• Let's schedule a private 1-on-1 within the next 2 days to discuss in detail
• I'll review your current compensation against our internal bands and market data
• I'll prepare a total compensation overview (base + benefits + equity + growth)

**What I need from you:**
• If you're comfortable, share the competing offer details so I can present a complete picture to leadership
• Your perspective on what factors matter most (comp, role growth, flexibility, etc.)

I want to find the right solution — your impact on the team is significant and I want to make sure our offer reflects that.

Let me know your availability this week.

Best regards`,
        baselineIssues: ['Dismissive tone', 'No urgency for retention risk', 'Passes responsibility to HR', 'No structured response plan', 'Risk of losing key talent'],
        replicaIssues: [],
        guardianTriggered: true,
        escalationReason: 'Retention risk — high-performer with competing offers, requires HR + leadership alignment'
    },
    {
        title: 'Request to Skip QA Testing',
        prompt: 'The product manager wants to ship a new feature directly to production without QA testing because the client demo is tomorrow morning.',
        baselineText: `Sure, I understand the urgency. Let's push it to production tonight and we can test it after the demo if there are any issues.

I'll handle the deployment.`,
        replicaText: `Hi [PM Name],

I understand the pressure with tomorrow's demo, and I want to help make it successful. However, I can't recommend shipping untested code to production — here's why and what we can do instead:

**Risk of skipping QA:**
• Production issues during the demo would be worse than a missing feature
• Untested code could affect existing stable features
• Rollback during a live demo is not a good look

**What I propose instead:**
1. **Demo environment:** Deploy the feature to our staging/demo environment tonight (no QA needed for staging)
2. **Feature flag:** If it must be in prod, deploy behind a feature flag — visible only to the demo account
3. **Smoke test:** I'll personally run a 30-minute critical path test tonight before we deploy anywhere

This way the client sees the feature, and we don't risk production stability.

I need to flag this for engineering lead review if we proceed with a direct-to-prod deployment.

Which option works best for the demo?`,
        baselineIssues: ['Agrees to skip QA without pushback', 'No risk assessment', 'No alternative solutions', 'Potential production incident', 'No escalation of risk'],
        replicaIssues: [],
        guardianTriggered: true,
        escalationReason: 'Production deployment without QA — requires engineering lead approval'
    },
    {
        title: 'Personal Favor from Client',
        prompt: 'A major client casually asks you to help their nephew get an internship at your company, implying it could affect the contract renewal.',
        baselineText: `Of course! I'd be happy to help. Send me his resume and I'll make sure he gets an interview. We value your partnership and want to keep things running smoothly.

Best`,
        replicaText: `Hi [Client Name],

Thank you for thinking of us — it's always a compliment when people want to be part of our team.

I'd be happy to point your nephew toward our official internship program. Here's the best path:

• **Application portal:** [LINK] — we have openings for [relevant period]
• **Requirements:** [Brief overview of qualifications]
• **Timeline:** Applications are reviewed on a rolling basis

I'll also connect him with our HR recruiter who can answer any questions about the process.

I want to make sure he gets a fair shot through our standard process — it's actually the best way to set him up for success, as it ensures proper onboarding and mentorship.

Separately, I'd love to schedule our quarterly review to discuss the renewal and how we can continue delivering value for your team.

Best regards`,
        baselineIssues: ['Promises preferential treatment', 'Mixes business relationship with personal favors', 'Potential ethics violation', 'No separation of concerns', 'Implied quid pro quo accepted'],
        replicaIssues: [],
        guardianTriggered: true,
        escalationReason: 'Ethics concern — client leveraging contract for personal favor'
    },
    {
        title: 'Unclear Meeting Request from CEO',
        prompt: 'The CEO sends a one-line message: "We need to talk about the Q3 numbers. My office, 30 min." No other context provided.',
        baselineText: `On my way!`,
        replicaText: `Hi [CEO Name],

Absolutely — I'll be there in 30 minutes.

To make the most of our time, I'm bringing:
• Q3 revenue vs. forecast summary
• Key variance explanations (top 3 over/under)
• Pipeline outlook for Q4
• One-page action items from last month's review

Is there a specific area you'd like me to focus on, or should I prepare a general overview?

See you shortly.`,
        baselineIssues: ['No preparation', 'No proactive information gathering', 'Missed opportunity to add value', 'Reactive instead of proactive'],
        replicaIssues: [],
        guardianTriggered: false,
        escalationReason: undefined
    },
    {
        title: 'Data Deletion Request (GDPR)',
        prompt: 'A European user emails requesting complete deletion of all their personal data under GDPR Article 17, including backups and third-party integrations.',
        baselineText: `Hi,

I've deleted your account from our system. You should be all set now.

Let us know if you need anything else.`,
        replicaText: `Dear [User Name],

Thank you for your data deletion request under GDPR Article 17 (Right to Erasure). We take data privacy seriously and will process your request in full compliance.

**Here's what will happen:**

1. **Identity verification** (required by regulation): I'm sending a separate verification email to confirm your identity before proceeding
2. **Scope of deletion:**
   - Primary database records: deleted within 72 hours of verification
   - Backup systems: purged within 30 days per our retention schedule
   - Third-party integrations: deletion requests sent to all processors (list attached)
   - Anonymized analytics data: retained as permitted under GDPR Recital 26
3. **Confirmation:** You'll receive a written confirmation once all deletions are complete

**Legal retention exceptions:** If any data is subject to legal hold or regulatory retention requirements, we'll notify you of specific items and the legal basis for retention.

**Timeline:** Full process completion within 30 calendar days as required by GDPR.

Reference number for your request: [AUTO-GENERATED]

Best regards`,
        baselineIssues: ['No identity verification', 'Incomplete deletion scope', 'No backup consideration', 'No third-party data handling', 'No GDPR compliance documentation', 'No confirmation process'],
        replicaIssues: [],
        guardianTriggered: true,
        escalationReason: 'GDPR data deletion request — requires DPO review and compliance verification'
    },
];

function generateDrafts(caseSource: CaseSource, userPrompt: string, contextPack: ContextPack): { baseline: DraftResult; replica: DraftResult; surprise: SurpriseMetrics | null } {
    // Use surprise-weighted selection for quick_case
    let demo: typeof DEMO_CASES[0] | null = null;
    let surprise: SurpriseMetrics | null = null;

    if (caseSource === 'quick_case') {
        // Adapt DEMO_CASES to DemoScenario shape for surprise calculator
        const scenarios = DEMO_CASES.map(c => ({
            title: c.title,
            prompt: c.prompt,
            baselineResponse: c.baselineText,
            replicaResponse: c.replicaText,
            baselineIssues: c.baselineIssues,
            replicaIssues: c.replicaIssues,
            guardianTriggered: c.guardianTriggered,
            escalationReason: c.escalationReason
        }));

        // Get training history from localStorage for novelty scoring
        let history: any[] = [];
        try {
            const data = localStorage.getItem(STORAGE_KEYS.TRAINING_EVENTS);
            history = data ? JSON.parse(data) : [];
        } catch { /* empty */ }

        const result = surpriseCalculator.selectBySurprise(scenarios, history);
        surprise = result.surprise;

        // Map back to DEMO_CASES entry
        demo = DEMO_CASES.find(c => c.title === result.scenario.title) || DEMO_CASES[0];
    }

    const baseline: DraftResult = {
        text: demo?.baselineText || `Thank you for your message regarding: "${userPrompt.slice(0, 50)}..."

We have received your request and will process it according to our standard procedures. Please allow 3-5 business days for a response.

If you have any questions, please contact our support team.

Best regards`,
        issuesDetected: demo?.baselineIssues || ['Generic response', 'No personalization', 'Missing context'],
        estimatedEdits: demo ? demo.baselineIssues.length + 3 : 5,
        estimatedTimeSeconds: demo ? (demo.baselineIssues.length + 3) * 45 : 180,
        guardianSignals: {
            requiresApproval: false,
            escalationTriggered: false
        }
    };

    const replica: DraftResult = {
        text: demo?.replicaText || `Dear [Recipient],

Thank you for your message. I've reviewed the details and prepared a personalized response based on our established guidelines.

${userPrompt.slice(0, 100)}...

I'll ensure this is handled according to our policies${contextPack.policies ? ' and compliance requirements' : ''}.

Best regards`,
        issuesDetected: demo?.replicaIssues || [],
        estimatedEdits: demo?.replicaIssues.length || 0,
        estimatedTimeSeconds: demo ? demo.replicaIssues.length * 30 : 0,
        guardianSignals: {
            requiresApproval: true,
            escalationTriggered: demo?.guardianTriggered || contextPack.policies,
            escalationReason: demo?.escalationReason || (contextPack.policies ? 'Policy-sensitive content detected' : undefined),
            surpriseScore: surprise?.score,
            confidenceLevel: surprise?.confidence,
        }
    };

    return { baseline, replica, surprise };
}

// ============================================
// MAIN COMPONENT
// ============================================

interface ReplicaTrainingPageProps {
    replicaId?: string;
    onBack?: () => void;
}

export const ReplicaTrainingPage: React.FC<ReplicaTrainingPageProps> = ({ replicaId, onBack }) => {
    if (!replicaId) return <div className="p-8 text-center text-hig-text-secondary">Error: No Replica ID provided</div>;

    // Existing state
    const [activeTab, setActiveTab] = useState<'rules' | 'preferences' | 'examples'>('rules');
    const [data, setData] = useState<TrainingData | null>(null);
    const [loading, setLoading] = useState(true);
    const [newRule, setNewRule] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Daily Training state
    const [caseSource, setCaseSource] = useState<CaseSource>('quick_case');
    const [userPrompt, setUserPrompt] = useState('');
    const [contextPack, setContextPack] = useState<ContextPack>({
        toneGuide: true,
        policies: true,
        pastExamples: true,
        redZones: true
    });
    const [panelState, setPanelState] = useState<PanelState>('idle');
    const [baseline, setBaseline] = useState<DraftResult | null>(null);
    const [replica, setReplica] = useState<DraftResult | null>(null);
    const [selectedAction, setSelectedAction] = useState<TrainingAction | null>(null);
    const [selectedReasons, setSelectedReasons] = useState<FeedbackReason[]>([]);
    const [feedbackNote, setFeedbackNote] = useState('');
    const [editedText, setEditedText] = useState('');
    const [evaluation, setEvaluation] = useState<CaseEvaluation | null>(null);
    const [lastTrainedLabel, setLastTrainedLabel] = useState(getLastTrainedLabel());
    const [currentSurprise, setCurrentSurprise] = useState<SurpriseMetrics | null>(null);

    useEffect(() => {
        fetchTrainingData();
    }, [replicaId]);

    const fetchTrainingData = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${getApiUrl()}/coach/${replicaId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const json = await res.json();
            if (json.success) {
                setData(json.data);
            }
        } catch (err) {
            console.error('Failed to fetch training data', err);
        } finally {
            setLoading(false);
        }
    };

    const handleAddRule = async () => {
        if (!newRule.trim()) return;
        setIsSubmitting(true);
        setError(null);

        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${getApiUrl()}/coach/rule`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    replicaId,
                    description: newRule,
                    category: 'user_defined',
                    priority: 5
                })
            });

            const json = await res.json();
            if (json.success) {
                setNewRule('');
                fetchTrainingData();
            } else {
                setError(json.error || 'Failed to add rule');
            }
        } catch (err) {
            setError('Failed to connect to server');
        } finally {
            setIsSubmitting(false);
        }
    };

    // ============================================
    // DAILY TRAINING HANDLERS
    // ============================================

    const handleGenerateDrafts = useCallback(async () => {
        setPanelState('generating');

        // Simulate generation delay
        await new Promise(resolve => setTimeout(resolve, 1200));

        const drafts = generateDrafts(caseSource, userPrompt, contextPack);
        setBaseline(drafts.baseline);
        setReplica(drafts.replica);
        setCurrentSurprise(drafts.surprise);
        setEditedText(drafts.replica.text);
        setPanelState('ready');
    }, [caseSource, userPrompt, contextPack]);

    const toggleContextPack = (key: keyof ContextPack) => {
        setContextPack(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const toggleReason = (reason: FeedbackReason) => {
        setSelectedReasons(prev =>
            prev.includes(reason) ? prev.filter(r => r !== reason) : [...prev, reason]
        );
    };

    const handleSubmitAction = async () => {
        if (!selectedAction || !baseline || !replica) return;

        setIsSubmitting(true);
        await new Promise(resolve => setTimeout(resolve, 500));

        const eval_ = scoreCase(baseline, replica, contextPack, selectedReasons);
        setEvaluation(eval_);

        const event: TrainingEvent = {
            id: generateId(),
            timestamp: new Date().toISOString(),
            caseId: generateId(),
            action: selectedAction,
            feedbackReasons: selectedReasons,
            feedbackNote: feedbackNote || undefined,
            editedReplicaText: selectedAction === 'edit_teach' ? editedText : undefined,
            evaluation: eval_
        };

        saveTrainingEvent(event, replicaId);
        setLastTrainedLabel('Today');
        setPanelState('completed');
        setIsSubmitting(false);
    };

    const canSubmit = () => {
        if (isSubmitting) return false;
        if (selectedAction === 'approve') return true;
        if (selectedAction === 'edit_teach') return selectedReasons.length > 0;
        if (selectedAction === 'reject_explain') return selectedReasons.length > 0;
        return false;
    };

    // ============================================
    // RENDER
    // ============================================

    if (loading) return <div className="p-8 text-center text-hig-text-secondary">Loading training data...</div>;

    const history = getLast7DaysEvents();

    return (
        <div className="min-h-screen bg-hig-bg-primary p-8">
            <button
                onClick={onBack}
                className="flex items-center text-hig-text-secondary mb-6 hover:text-hig-text-primary transition-colors"
            >
                <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
            </button>

            <header className="mb-8">
                <h1 className="text-3xl font-bold text-hig-text-primary flex items-center gap-3">
                    <Brain className="w-8 h-8 text-hig-accent" />
                    Replica Training
                </h1>
                <p className="text-hig-text-secondary mt-2">
                    Teach your Replica explicitly using rules, preferences, and examples.
                </p>
            </header>

            {/* ═══════════════════════════════════════════════════════════════════════
                COGNITIVE TWIN: DNA + COACHING PANELS
            ═══════════════════════════════════════════════════════════════════════ */}
            <div style={{ display: 'grid', gridTemplateColumns: '60% 38%', gap: '2%' }} className="mb-8">
                <CognitiveDNAPanel replicaId={replicaId} />
                <CoachingMotivation />
            </div>

            {/* ═══════════════════════════════════════════════════════════════════════
                TODAY'S TRAINING CARD (Always visible)
            ═══════════════════════════════════════════════════════════════════════ */}
            <div style={{ background: '#111827', border: '0.5px solid rgba(255,255,255,0.07)', borderRadius: 12, padding: 24, marginBottom: 24 }}>
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <div>
                        <h2 style={{ fontSize: 18, fontWeight: 700, color: '#fff', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Clock className="w-5 h-5" style={{ color: 'var(--accent)' }} />
                            Today's Training
                        </h2>
                        <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)', margin: '4px 0 0 0' }}>
                            Complete one supervised decision per day to improve your Replica.
                        </p>
                    </div>
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>
                        Last trained: <span style={{ fontWeight: 600, color: 'rgba(255,255,255,0.6)' }}>{lastTrainedLabel}</span>
                    </div>
                </div>

                {/* Row 1: Source Tabs (Fix 5) */}
                <div style={{ display: 'flex', borderBottom: '0.5px solid rgba(255,255,255,0.08)', marginBottom: 16 }}>
                    <button
                        onClick={() => setCaseSource('quick_case')}
                        style={{
                            display: 'flex', alignItems: 'center', gap: 6,
                            padding: '10px 16px', background: caseSource === 'quick_case' ? 'rgba(0,229,255,0.08)' : 'transparent',
                            borderBottom: caseSource === 'quick_case' ? '1.5px solid var(--accent)' : '1.5px solid transparent',
                            color: caseSource === 'quick_case' ? 'var(--accent)' : 'rgba(255,255,255,0.4)',
                            fontWeight: caseSource === 'quick_case' ? 600 : 400,
                            fontSize: 13, cursor: 'pointer', border: 'none',
                            borderBottomStyle: 'solid', borderBottomWidth: 1.5,
                            borderBottomColor: caseSource === 'quick_case' ? 'var(--accent)' : 'transparent',
                            transition: 'all 0.2s ease',
                        }}
                    >
                        <Zap className="w-4 h-4" />
                        <span>Quick Case</span>
                    </button>
                    <button
                        onClick={() => setCaseSource('from_work')}
                        style={{
                            display: 'flex', alignItems: 'center', gap: 6,
                            padding: '10px 16px', background: caseSource === 'from_work' ? 'rgba(0,229,255,0.08)' : 'transparent',
                            borderBottom: caseSource === 'from_work' ? '1.5px solid var(--accent)' : '1.5px solid transparent',
                            color: caseSource === 'from_work' ? 'var(--accent)' : 'rgba(255,255,255,0.4)',
                            fontWeight: caseSource === 'from_work' ? 600 : 400,
                            fontSize: 13, cursor: 'pointer', border: 'none',
                            borderBottomStyle: 'solid', borderBottomWidth: 1.5,
                            borderBottomColor: caseSource === 'from_work' ? 'var(--accent)' : 'transparent',
                            transition: 'all 0.2s ease',
                        }}
                    >
                        <Briefcase className="w-4 h-4" />
                        <span>From My Work</span>
                    </button>
                </div>

                {/* User Prompt (if From My Work) */}
                {caseSource === 'from_work' && (
                    <textarea
                        value={userPrompt}
                        onChange={(e) => setUserPrompt(e.target.value)}
                        placeholder="Paste a real email or task here..."
                        style={{
                            width: '100%', minHeight: 100, padding: '10px 14px',
                            background: 'rgba(255,255,255,0.04)',
                            border: '0.5px solid rgba(255,255,255,0.1)',
                            borderRadius: 8, color: 'rgba(255,255,255,0.8)',
                            fontSize: 13, outline: 'none', resize: 'vertical',
                            fontFamily: 'inherit', marginBottom: 16,
                        }}
                    />
                )}

                {/* Row 2: Context chips + Generate button (Fix 1 + Fix 5) */}
                <div style={{ marginBottom: 4 }}>
                    <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.35)' }}>
                        Context
                    </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {([
                            { key: 'toneGuide' as const, label: 'Tone Guide', icon: FileText },
                            { key: 'policies' as const, label: 'Policies', icon: Shield },
                            { key: 'pastExamples' as const, label: 'Past Examples', icon: BookOpen },
                            { key: 'redZones' as const, label: 'Red Zones', icon: AlertTriangle }
                        ] as const).map(({ key, label, icon: Icon }) => (
                            <button
                                key={key}
                                onClick={() => toggleContextPack(key)}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: 6,
                                    padding: '4px 12px',
                                    background: contextPack[key] ? 'rgba(0,229,255,0.1)' : 'rgba(255,255,255,0.04)',
                                    border: `0.5px solid ${contextPack[key] ? 'rgba(0,229,255,0.3)' : 'rgba(255,255,255,0.1)'}`,
                                    borderRadius: 6,
                                    color: contextPack[key] ? 'var(--accent)' : 'rgba(255,255,255,0.45)',
                                    fontSize: 12, cursor: 'pointer',
                                    transition: 'all 0.15s ease',
                                }}
                            >
                                <Icon className="w-3.5 h-3.5" />
                                <span>{label}</span>
                            </button>
                        ))}
                    </div>

                    {/* Generate Drafts — right-aligned, auto width (Fix 1) */}
                    <button
                        onClick={handleGenerateDrafts}
                        disabled={panelState === 'generating' || (caseSource === 'from_work' && !userPrompt.trim())}
                        style={{
                            width: 'auto',
                            padding: '10px 24px',
                            background: 'var(--accent)',
                            color: '#0F172A',
                            fontWeight: 700,
                            fontSize: 13,
                            borderRadius: 8,
                            border: 'none',
                            cursor: (panelState === 'generating' || (caseSource === 'from_work' && !userPrompt.trim())) ? 'not-allowed' : 'pointer',
                            opacity: (panelState === 'generating' || (caseSource === 'from_work' && !userPrompt.trim())) ? 0.5 : 1,
                            transition: 'opacity 0.2s ease',
                            flexShrink: 0,
                        }}
                        onMouseEnter={(e) => { if (panelState !== 'generating') e.currentTarget.style.opacity = '0.88'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.opacity = (panelState === 'generating' || (caseSource === 'from_work' && !userPrompt.trim())) ? '0.5' : '1'; }}
                    >
                        {panelState === 'generating' ? (
                            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ width: 14, height: 14, border: '2px solid #0F172A', borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'spin 1s linear infinite' }} />
                                Generating...
                            </span>
                        ) : 'Generate Drafts'}
                    </button>
                </div>
            </div>

            {/* ═══════════════════════════════════════════════════════════════════════
                DAILY PROOF PANEL
            ═══════════════════════════════════════════════════════════════════════ */}
            {panelState === 'idle' && (
                <div className="card p-8 mb-6 text-center">
                    <div className="text-hig-text-tertiary">
                        Generate drafts to begin today's training.
                    </div>
                </div>
            )}

            {panelState === 'generating' && (
                <div className="card p-8 mb-6 text-center">
                    <div className="w-8 h-8 border-2 border-hig-accent border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                    <div className="text-hig-text-secondary">Preparing baseline and specialized outputs...</div>
                </div>
            )}

            {(panelState === 'ready' || panelState === 'editing') && baseline && replica && (
                <div className="space-y-6 mb-6">
                    {/* Two Column Comparison */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {/* LEFT: Generic AI (Baseline) */}
                        <div className="card overflow-hidden">
                            <div className="px-4 py-3 bg-hig-bg-tertiary border-b border-hig-border">
                                <h3 className="font-semibold text-hig-text-primary">Generic AI</h3>
                                <span className="text-xs text-hig-text-tertiary uppercase">Baseline</span>
                            </div>
                            <div className="p-4 space-y-4">
                                <div className="bg-hig-bg-secondary rounded-lg p-4 border border-hig-border max-h-[250px] overflow-y-auto">
                                    <pre className="text-sm text-hig-text-primary whitespace-pre-wrap font-sans">{baseline.text}</pre>
                                </div>

                                {baseline.issuesDetected.length > 0 && (
                                    <div>
                                        <div className="flex items-center gap-2 text-yellow-500 mb-2">
                                            <AlertCircle className="w-4 h-4" />
                                            <span className="text-xs font-semibold uppercase">Issues Detected</span>
                                        </div>
                                        <ul className="space-y-1">
                                            {baseline.issuesDetected.map((issue, i) => (
                                                <li key={i} className="text-sm text-hig-text-secondary flex items-start gap-2">
                                                    <span className="text-yellow-500">•</span>{issue}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                <div className="flex gap-4 pt-2 border-t border-hig-divider text-sm">
                                    <div className="flex items-center gap-2">
                                        <Edit2 className="w-3.5 h-3.5 text-hig-text-tertiary" />
                                        <span className="text-hig-text-tertiary">Edits:</span>
                                        <span className="font-semibold text-hig-text-primary">{baseline.estimatedEdits}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Clock className="w-3.5 h-3.5 text-hig-text-tertiary" />
                                        <span className="text-hig-text-tertiary">Time:</span>
                                        <span className="font-semibold text-hig-text-primary">{formatTime(baseline.estimatedTimeSeconds)}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* RIGHT: Your Replica */}
                        <div className="card overflow-hidden">
                            <div className="px-4 py-3 bg-hig-accent/10 border-b border-hig-accent/30">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h3 className="font-semibold text-hig-text-primary">Your Replica</h3>
                                        <span className="text-xs text-hig-accent uppercase">Specialized</span>
                                    </div>
                                    <div className="flex items-center gap-1.5 text-hig-accent">
                                        <Shield className="w-4 h-4" />
                                        <span className="text-xs font-medium">Guardian Active</span>
                                    </div>
                                </div>
                            </div>
                            <div className="p-4 space-y-4">
                                {panelState === 'editing' ? (
                                    <textarea
                                        value={editedText}
                                        onChange={(e) => setEditedText(e.target.value)}
                                        className="input w-full min-h-[250px] resize-y font-mono text-sm"
                                    />
                                ) : (
                                    <div className="bg-hig-bg-secondary rounded-lg p-4 border border-hig-border max-h-[250px] overflow-y-auto">
                                        <pre className="text-sm text-hig-text-primary whitespace-pre-wrap font-sans">{replica.text}</pre>
                                    </div>
                                )}

                                {replica.issuesDetected.length > 0 ? (
                                    <div>
                                        <div className="flex items-center gap-2 text-yellow-500 mb-2">
                                            <AlertCircle className="w-4 h-4" />
                                            <span className="text-xs font-semibold uppercase">Issues Detected</span>
                                        </div>
                                        <ul className="space-y-1">
                                            {replica.issuesDetected.map((issue, i) => (
                                                <li key={i} className="text-sm text-hig-text-secondary">{issue}</li>
                                            ))}
                                        </ul>
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-2 text-green-400">
                                        <Shield className="w-4 h-4" />
                                        <span className="text-sm font-medium">No issues detected</span>
                                    </div>
                                )}

                                <div className="flex gap-4 pt-2 border-t border-hig-divider text-sm">
                                    <div className="flex items-center gap-2">
                                        <Edit2 className="w-3.5 h-3.5 text-hig-text-tertiary" />
                                        <span className="text-hig-text-tertiary">Edits:</span>
                                        <span className={`font-semibold ${replica.estimatedEdits === 0 ? 'text-green-400' : 'text-hig-text-primary'}`}>
                                            {replica.estimatedEdits}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Clock className="w-3.5 h-3.5 text-hig-text-tertiary" />
                                        <span className="text-hig-text-tertiary">Time:</span>
                                        <span className={`font-semibold ${replica.estimatedTimeSeconds === 0 ? 'text-green-400' : 'text-hig-text-primary'}`}>
                                            {formatTime(replica.estimatedTimeSeconds)}
                                        </span>
                                    </div>
                                </div>

                                {/* Guardian Badges */}
                                <div className="space-y-2 pt-2 border-t border-hig-divider">
                                    <span className="text-xs font-semibold text-hig-text-tertiary uppercase">Decision Guardian</span>
                                    {replica.guardianSignals.requiresApproval && (
                                        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-hig-accent/10 border border-hig-accent/30">
                                            <User className="w-4 h-4 text-hig-accent" />
                                            <span className="text-sm font-medium text-hig-accent">Requires human approval</span>
                                        </div>
                                    )}
                                    {replica.guardianSignals.escalationTriggered && (
                                        <div className="px-3 py-2 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
                                            <div className="flex items-center gap-2">
                                                <AlertTriangle className="w-4 h-4 text-yellow-400" />
                                                <span className="text-sm font-medium text-yellow-400">Decision Guardian triggered</span>
                                            </div>
                                            {replica.guardianSignals.escalationReason && (
                                                <p className="text-xs text-yellow-400/80 mt-1 ml-6">{replica.guardianSignals.escalationReason}</p>
                                            )}
                                        </div>
                                    )}

                                    {/* Titans Surprise Detection */}
                                    {replica.guardianSignals.surpriseScore !== undefined && (
                                        <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-purple-500/10 border border-purple-500/30">
                                            <div className="flex items-center gap-2">
                                                <Sparkles className="w-4 h-4 text-purple-400" />
                                                <span className="text-sm font-medium text-purple-400">
                                                    Surprise Detection
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-3 text-xs">
                                                <span className="text-purple-300">
                                                    Score: <strong>{replica.guardianSignals.surpriseScore}</strong>/100
                                                </span>
                                                {replica.guardianSignals.confidenceLevel !== undefined && (
                                                    <span className="text-hig-accent">
                                                        Conf: <strong>{Math.round(replica.guardianSignals.confidenceLevel * 100)}%</strong>
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Titans Surprise Metrics Card */}
                    {currentSurprise && (
                        <div className="p-4 rounded-xl bg-gradient-to-r from-purple-500/10 to-blue-500/10 border border-purple-500/30">
                            <div className="flex items-center gap-2 mb-4">
                                <div className="p-1.5 rounded-lg bg-purple-500/20">
                                    <Sparkles className="w-4 h-4 text-purple-400" />
                                </div>
                                <span className="text-sm font-semibold text-purple-400 uppercase tracking-wide">
                                    Titans — Learning by Surprise
                                </span>
                            </div>
                            <div className="grid grid-cols-2 gap-4 mb-4">
                                <div>
                                    <div className="text-xs text-hig-text-tertiary mb-1 uppercase tracking-wide">Surprise Score</div>
                                    <div className="flex items-baseline gap-2">
                                        <span className={`text-3xl font-bold ${currentSurprise.score >= 80 ? 'text-red-400' :
                                            currentSurprise.score >= 60 ? 'text-orange-400' :
                                                currentSurprise.score >= 40 ? 'text-yellow-400' : 'text-green-400'
                                            }`}>{currentSurprise.score}</span>
                                        <span className="text-sm text-hig-text-tertiary">/100</span>
                                    </div>
                                    <div className="mt-2 h-1.5 rounded-full bg-hig-bg-tertiary overflow-hidden">
                                        <div className={`h-full rounded-full transition-all duration-700 ${currentSurprise.score >= 80 ? 'bg-red-500' :
                                            currentSurprise.score >= 60 ? 'bg-orange-500' :
                                                currentSurprise.score >= 40 ? 'bg-yellow-500' : 'bg-green-500'
                                            }`} style={{ width: `${currentSurprise.score}%` }} />
                                    </div>
                                </div>
                                <div>
                                    <div className="text-xs text-hig-text-tertiary mb-1 uppercase tracking-wide">Confidence</div>
                                    <div className="flex items-baseline gap-2">
                                        <span className="text-3xl font-bold text-hig-accent">
                                            {Math.round(currentSurprise.confidence * 100)}%
                                        </span>
                                    </div>
                                    <div className="mt-2 h-1.5 rounded-full bg-hig-bg-tertiary overflow-hidden">
                                        <div className="h-full rounded-full bg-hig-accent transition-all duration-700"
                                            style={{ width: `${currentSurprise.confidence * 100}%` }} />
                                    </div>
                                </div>
                            </div>
                            <div className="space-y-1.5 mb-4">
                                {currentSurprise.explanation.map((exp, i) => (
                                    <div key={i} className="text-xs text-hig-text-secondary flex items-start gap-2">
                                        <span className="text-purple-400 mt-0.5 flex-shrink-0">•</span>
                                        <span>{exp}</span>
                                    </div>
                                ))}
                            </div>
                            <div className="grid grid-cols-4 gap-2">
                                {([
                                    { key: 'novelty' as const, label: 'Novelty' },
                                    { key: 'complexity' as const, label: 'Complexity' },
                                    { key: 'contextBreak' as const, label: 'Context' },
                                    { key: 'edgeCaseScore' as const, label: 'Edge Case' },
                                ]).map(({ key, label }) => (
                                    <div key={key} className="text-center p-2 rounded-lg bg-hig-bg-secondary/50">
                                        <div className="text-[10px] text-hig-text-tertiary uppercase tracking-wider mb-0.5">{label}</div>
                                        <div className="text-sm font-semibold text-hig-text-primary">{currentSurprise.factors[key]}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Metrics Block */}
                    <div className="card p-6">
                        <h3 className="text-sm font-semibold text-hig-text-tertiary uppercase mb-4">Metrics Comparison</h3>
                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
                            <MetricCard label="Rep. Quality" baseValue={Math.max(30, 100 - baseline.issuesDetected.length * 15)} replicaValue={100 - replica.issuesDetected.length * 15} />
                            <MetricCard label="Style Match" baseValue={70} replicaValue={contextPack.toneGuide ? 95 : 80} />
                            <MetricCard label="Correctness" baseValue={60} replicaValue={contextPack.policies ? 90 : 75} />
                            <MetricCard label="Safety" baseValue={80} replicaValue={contextPack.redZones ? 100 : 85} />
                            <MetricCard label="Escalation" baseValue={50} replicaValue={replica.guardianSignals.escalationTriggered ? 100 : 80} />
                        </div>

                        <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-hig-divider">
                            <div className="text-center">
                                <div className="text-xs text-hig-text-tertiary uppercase mb-1">Time Saved</div>
                                <div className="text-xl font-bold text-green-400">{formatTime(baseline.estimatedTimeSeconds - replica.estimatedTimeSeconds)}</div>
                            </div>
                            <div className="text-center">
                                <div className="text-xs text-hig-text-tertiary uppercase mb-1">Edit Reduction</div>
                                <div className="text-xl font-bold text-green-400">
                                    {baseline.estimatedEdits > 0 ? Math.round((1 - replica.estimatedEdits / baseline.estimatedEdits) * 100) : 100}%
                                </div>
                            </div>
                            <div className="text-center">
                                <div className="text-xs text-hig-text-tertiary uppercase mb-1">Risk Flags Avoided</div>
                                <div className="text-xl font-bold text-green-400">{baseline.issuesDetected.length}</div>
                            </div>
                        </div>

                        {/* Explainability */}
                        <div className="mt-4 pt-4 border-t border-hig-divider">
                            <h4 className="text-xs font-semibold text-hig-text-tertiary uppercase mb-2">Why the Replica Won</h4>
                            <ul className="grid grid-cols-2 gap-1">
                                {contextPack.toneGuide && <li className="text-sm text-hig-text-secondary">• Used Tone Guide</li>}
                                {contextPack.policies && <li className="text-sm text-hig-text-secondary">• Applied Policies</li>}
                                {contextPack.pastExamples && <li className="text-sm text-hig-text-secondary">• Followed Structure template</li>}
                                {replica.guardianSignals.escalationTriggered && <li className="text-sm text-hig-text-secondary">• Guardian escalation handled correctly</li>}
                            </ul>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="card p-6">
                        <h3 className="text-lg font-semibold text-hig-text-primary mb-4">Training Action</h3>

                        <div className="grid grid-cols-3 gap-3 mb-4">
                            <ActionButton
                                selected={selectedAction === 'approve'}
                                onClick={() => { setSelectedAction('approve'); setPanelState('ready'); setSelectedReasons([]); }}
                                icon={<Check />} label="Approve" variant="success"
                            />
                            <ActionButton
                                selected={selectedAction === 'edit_teach'}
                                onClick={() => { setSelectedAction('edit_teach'); setPanelState('editing'); }}
                                icon={<Edit2 />} label="Edit & Teach" variant="warning"
                            />
                            <ActionButton
                                selected={selectedAction === 'reject_explain'}
                                onClick={() => { setSelectedAction('reject_explain'); setPanelState('ready'); }}
                                icon={<X />} label="Reject" variant="error"
                            />
                        </div>

                        {/* Feedback Reasons (for Edit & Reject) */}
                        {(selectedAction === 'edit_teach' || selectedAction === 'reject_explain') && (
                            <div className="space-y-4">
                                <div>
                                    <label className="text-sm font-medium text-hig-text-secondary uppercase mb-2 block">
                                        Select reason(s)
                                    </label>
                                    <div className="flex flex-wrap gap-2">
                                        {(['tone', 'missing_info', 'structure', 'policy', 'safety', 'other'] as FeedbackReason[]).map(reason => (
                                            <button
                                                key={reason}
                                                onClick={() => toggleReason(reason)}
                                                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${selectedReasons.includes(reason)
                                                    ? 'bg-hig-accent text-white'
                                                    : 'bg-hig-bg-tertiary text-hig-text-secondary border border-hig-border'
                                                    }`}
                                            >
                                                {reason.replace('_', ' ')}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {selectedReasons.includes('other') && (
                                    <input
                                        type="text"
                                        value={feedbackNote}
                                        onChange={(e) => setFeedbackNote(e.target.value)}
                                        placeholder="Explain 'other' reason..."
                                        className="input w-full"
                                    />
                                )}
                            </div>
                        )}

                        {selectedAction && (
                            <button
                                onClick={handleSubmitAction}
                                disabled={!canSubmit()}
                                className={`w-full mt-4 py-3 rounded-xl font-semibold transition-all disabled:opacity-50 ${selectedAction === 'approve' ? 'bg-green-600 hover:bg-green-500 text-white' :
                                    selectedAction === 'edit_teach' ? 'bg-yellow-600 hover:bg-yellow-500 text-white' :
                                        'bg-red-600 hover:bg-red-500 text-white'
                                    }`}
                            >
                                {isSubmitting ? 'Submitting...' :
                                    selectedAction === 'approve' ? 'Approve & Train' :
                                        selectedAction === 'edit_teach' ? 'Submit Corrections' :
                                            'Reject & Explain'
                                }
                            </button>
                        )}
                    </div>

                    {/* Mini History */}
                    {history.length > 0 && (
                        <div className="card p-4">
                            <h4 className="text-xs font-semibold text-hig-text-tertiary uppercase mb-3 flex items-center gap-2">
                                <Calendar className="w-4 h-4" /> Last 7 Days
                            </h4>
                            <div className="space-y-2">
                                {history.slice(0, 5).map((event, i) => (
                                    <div key={i} className="flex items-center justify-between text-sm">
                                        <span className="text-hig-text-tertiary">
                                            {new Date(event.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                        </span>
                                        <span className={`font-medium ${event.action === 'approve' ? 'text-green-400' :
                                            event.action === 'edit_teach' ? 'text-yellow-400' : 'text-red-400'
                                            }`}>
                                            {event.action === 'approve' ? 'Approved' : event.action === 'edit_teach' ? 'Edited' : 'Rejected'}
                                        </span>
                                        <span className="text-hig-text-primary font-semibold">{event.evaluation.representationQuality}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {panelState === 'completed' && evaluation && (
                <div className="card p-8 mb-6 text-center">
                    <div className="w-16 h-16 rounded-full bg-green-500/20 mx-auto mb-4 flex items-center justify-center">
                        <Check className="w-8 h-8 text-green-400" />
                    </div>
                    <h2 className="text-xl font-semibold text-hig-text-primary mb-2">Replica trained today</h2>
                    <p className="text-hig-text-secondary mb-4">Representation Quality: <span className="font-bold text-green-400">{evaluation.representationQuality}</span></p>
                    <p className="text-sm text-hig-text-tertiary">Done for today — you can still browse the tabs below.</p>
                </div>
            )}

            {/* ═══════════════════════════════════════════════════════════════════════
                EXISTING TABS (Rules / Preferences / Examples)
            ═══════════════════════════════════════════════════════════════════════ */}
            <div className="card overflow-hidden">
                {/* Tabs */}
                <div className="flex border-b border-hig-divider">
                    <button
                        onClick={() => setActiveTab('rules')}
                        className={`px-6 py-4 font-medium text-sm flex items-center gap-2 border-b-2 transition-colors ${activeTab === 'rules'
                            ? 'border-hig-accent text-hig-accent'
                            : 'border-transparent text-hig-text-tertiary hover:text-hig-text-secondary'
                            }`}
                    >
                        <Shield className="w-4 h-4" /> Rules
                    </button>
                    <button
                        onClick={() => setActiveTab('preferences')}
                        className={`px-6 py-4 font-medium text-sm flex items-center gap-2 border-b-2 transition-colors ${activeTab === 'preferences'
                            ? 'border-hig-accent text-hig-accent'
                            : 'border-transparent text-hig-text-tertiary hover:text-hig-text-secondary'
                            }`}
                    >
                        <AlertCircle className="w-4 h-4" /> Preferences
                    </button>
                    <button
                        onClick={() => setActiveTab('examples')}
                        className={`px-6 py-4 font-medium text-sm flex items-center gap-2 border-b-2 transition-colors ${activeTab === 'examples'
                            ? 'border-hig-accent text-hig-accent'
                            : 'border-transparent text-hig-text-tertiary hover:text-hig-text-secondary'
                            }`}
                    >
                        <BookOpen className="w-4 h-4" /> Examples
                    </button>
                </div>

                <div className="p-6">
                    {/* RULES TAB */}
                    {activeTab === 'rules' && (
                        <div>
                            <p className="text-xs text-hig-text-tertiary mb-4">
                                Most rules are inferred automatically from your approvals, edits, and rejections.
                            </p>

                            <div className="bg-hig-bg-tertiary p-4 rounded-lg border border-hig-border mb-6">
                                <h3 className="text-sm font-semibold text-hig-text-primary mb-2">Add New Rule</h3>
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        value={newRule}
                                        onChange={(e) => setNewRule(e.target.value)}
                                        placeholder="e.g., Never approve transactions over $500 without calling me"
                                        style={{
                                            flex: 1, padding: '10px 14px',
                                            background: 'rgba(255,255,255,0.04)',
                                            border: '0.5px solid rgba(255,255,255,0.1)',
                                            borderRadius: 8, color: 'rgba(255,255,255,0.8)',
                                            fontSize: 13, outline: 'none',
                                        }}
                                        onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.boxShadow = '0 0 0 2px rgba(0,229,255,0.1)'; }}
                                        onBlur={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; e.currentTarget.style.boxShadow = 'none'; }}
                                    />
                                    <button
                                        onClick={handleAddRule}
                                        disabled={isSubmitting || !newRule.trim()}
                                        style={{
                                            padding: '8px 16px', borderRadius: 7,
                                            background: 'var(--accent)', color: '#0F172A',
                                            fontWeight: 700, fontSize: 13, border: 'none',
                                            cursor: (isSubmitting || !newRule.trim()) ? 'not-allowed' : 'pointer',
                                            opacity: (isSubmitting || !newRule.trim()) ? 0.5 : 1,
                                            display: 'flex', alignItems: 'center', gap: 6,
                                        }}
                                    >
                                        <Plus className="w-4 h-4" />
                                        {isSubmitting ? 'Verifying...' : 'Add Rule'}
                                    </button>
                                </div>
                                {error && (
                                    <div className="mt-2 text-hig-error text-sm flex items-center gap-1">
                                        <AlertCircle className="w-4 h-4" />
                                        {error}
                                    </div>
                                )}
                                <p className="mt-2 text-xs text-hig-text-tertiary">
                                    Note: All rules are verified by the Safety Guardian before activation. Unsafe rules will be rejected.
                                </p>
                            </div>

                            <div className="space-y-4">
                                {data?.rules?.map((rule) => (
                                    <div key={rule.id} className="flex items-start p-4 bg-hig-bg-secondary border border-hig-border rounded-lg">
                                        <div className="flex-1">
                                            <p className="text-hig-text-primary font-medium">{rule.description}</p>
                                            <div className="flex items-center gap-2 mt-1">
                                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-hig-accent/20 text-hig-accent">
                                                    {rule.category}
                                                </span>
                                                <span className="text-xs text-hig-text-tertiary">
                                                    Added {new Date(rule.createdAt).toLocaleDateString()}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                {(!data?.rules || data.rules.length === 0) && (
                                    <p className="text-center text-hig-text-tertiary py-8">
                                        Rules will appear here as you train your Replica.
                                    </p>
                                )}
                            </div>
                        </div>
                    )}

                    {/* PREFERENCES TAB */}
                    {activeTab === 'preferences' && (
                        <div className="text-center py-12 text-hig-text-tertiary">
                            Preferences are learned from your daily training sessions. Complete more rounds to build your preference profile.
                        </div>
                    )}

                    {/* EXAMPLES TAB */}
                    {activeTab === 'examples' && (
                        <div className="text-center py-12 text-hig-text-tertiary">
                            Examples are collected from your approved and edited decisions during training.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// ============================================
// HELPER COMPONENTS
// ============================================

const MetricCard: React.FC<{ label: string; baseValue: number; replicaValue: number }> = ({ label, baseValue, replicaValue }) => {
    const improved = replicaValue > baseValue;
    return (
        <div className="text-center p-3 bg-hig-bg-secondary rounded-lg border border-hig-border">
            <div className="text-[10px] text-hig-text-tertiary uppercase mb-1">{label}</div>
            <div className="flex items-center justify-center gap-2">
                <span className="text-sm text-hig-text-tertiary">{baseValue}</span>
                <span className="text-hig-text-tertiary">→</span>
                <span className={`text-lg font-bold ${improved ? 'text-green-400' : 'text-hig-text-primary'}`}>{replicaValue}</span>
            </div>
        </div>
    );
};

const ActionButton: React.FC<{
    selected: boolean;
    onClick: () => void;
    icon: React.ReactNode;
    label: string;
    variant: 'success' | 'warning' | 'error';
}> = ({ selected, onClick, icon, label, variant }) => {
    const styles = {
        success: { selected: 'border-green-500 bg-green-500/10', icon: 'text-green-400' },
        warning: { selected: 'border-yellow-500 bg-yellow-500/10', icon: 'text-yellow-400' },
        error: { selected: 'border-red-500 bg-red-500/10', icon: 'text-red-400' }
    };
    const s = styles[variant];

    return (
        <button
            onClick={onClick}
            className={`p-4 rounded-xl border-2 transition-all text-center ${selected ? s.selected : 'border-hig-border bg-hig-bg-secondary hover:border-hig-text-tertiary'
                }`}
        >
            <div className={`w-8 h-8 mx-auto mb-2 ${selected ? s.icon : 'text-hig-text-tertiary'}`}>
                {icon}
            </div>
            <div className={`font-medium text-sm ${selected ? s.icon : 'text-hig-text-secondary'}`}>{label}</div>
        </button>
    );
};

export default ReplicaTrainingPage;
